import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { LogOut, Search, Download, Eye, Trash2 } from 'lucide-react';
import { translations } from '../utils/translations';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [language] = useState('de');
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterInterest, setFilterInterest] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const navigate = useNavigate();
  const t = translations[language];

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchLeads();
  }, [navigate, searchTerm, filterInterest]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterInterest && filterInterest !== 'all') params.append('interest', filterInterest);
      
      const response = await fetch(`${BACKEND_URL}/api/leads?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch leads');
      }

      const data = await response.json();
      setLeads(data.leads || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load leads',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  const openDeleteDialog = (lead) => {
    setLeadToDelete(lead);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!leadToDelete) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete lead');
      }

      setLeads(leads.filter(lead => lead.id !== leadToDelete.id));
      toast.success('Lead erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setLeadToDelete(null);
    } catch (error) {
      toast.error('Fehler beim Löschen des Leads');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/leads/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export CSV',
        variant: 'destructive'
      });
    }
  };

  const filteredLeads = leads;

  const interestLabels = {
    admin: t.form.interestOptions.admin,
    sales: t.form.interestOptions.sales,
    partner: t.form.interestOptions.partner,
    customer: t.form.interestOptions.customer,
    investor: t.form.interestOptions.investor,
    other: t.form.interestOptions.other
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      {/* Header */}
      <nav className="bg-bg-secondary border-b border-border-subtle">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold text-accent-primary">
            smart-signage.com.br
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-text-secondary hover:text-accent-primary"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t.admin.logout}
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="display-md mb-2">{t.admin.title}</h1>
          <p className="body-lg text-text-secondary">
            {t.admin.total}: <span className="text-accent-primary font-semibold">{filteredLeads.length}</span>
          </p>
        </div>

        {/* Filters and Actions */}
        <Card className="feature-card p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block body-sm font-medium mb-2 text-text-secondary">{t.admin.search}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t.admin.search}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <label className="block body-sm font-medium mb-2 text-text-secondary">{t.admin.filter}</label>
              <Select value={filterInterest} onValueChange={setFilterInterest}>
                <SelectTrigger className="input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.admin.filterAll}</SelectItem>
                  <SelectItem value="admin">{t.form.interestOptions.admin}</SelectItem>
                  <SelectItem value="sales">{t.form.interestOptions.sales}</SelectItem>
                  <SelectItem value="partner">{t.form.interestOptions.partner}</SelectItem>
                  <SelectItem value="customer">{t.form.interestOptions.customer}</SelectItem>
                  <SelectItem value="investor">{t.form.interestOptions.investor}</SelectItem>
                  <SelectItem value="other">{t.form.interestOptions.other}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleExportCSV}
              className="btn-primary"
              disabled={filteredLeads.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              {t.admin.exportCSV}
            </Button>
          </div>
        </Card>

        {/* Leads Table */}
        {filteredLeads.length === 0 ? (
          <Card className="feature-card p-12 text-center">
            <p className="body-lg text-text-muted">{t.admin.noLeads}</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredLeads.map((lead) => (
              <Card key={lead.id} className="feature-card p-6 hover:border-accent-primary transition-all">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <h3 className="h3">{lead.name}</h3>
                      <span className="inline-block px-3 py-1 rounded-lg bg-accent-bg text-accent-primary body-sm font-medium w-fit">
                        {interestLabels[lead.interest]}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <p className="body-sm text-text-secondary">
                        <span className="font-medium text-text-primary">{t.admin.email}:</span> {lead.email}
                      </p>
                      {lead.whatsapp && (
                        <p className="body-sm text-text-secondary">
                          <span className="font-medium text-text-primary">WhatsApp:</span> {lead.whatsapp}
                        </p>
                      )}
                      <p className="body-sm text-text-secondary">
                        <span className="font-medium text-text-primary">{t.admin.location}:</span> {lead.location}
                      </p>
                      <p className="body-sm text-text-secondary">
                        <span className="font-medium text-text-primary">{t.admin.date}:</span> {new Date(lead.date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="body-sm font-medium text-text-primary mb-1">Why:</p>
                      <p className="body-sm text-text-secondary">{lead.why}</p>
                    </div>
                    {lead.linkedin && (
                      <p className="body-sm text-text-secondary">
                        <span className="font-medium text-text-primary">LinkedIn:</span>{' '}
                        <a href={lead.linkedin} target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">
                          {lead.linkedin}
                        </a>
                      </p>
                    )}
                  </div>
                  <div className="flex lg:flex-col gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn-ghost"
                      onClick={() => toast.info(`Ansehen: ${lead.name}`)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {t.admin.view}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-red-600 text-white hover:bg-red-700 transition-colors"
                      onClick={() => openDeleteDialog(lead)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t.admin.delete}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent className="bg-bg-secondary border-border-primary">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-text-primary">Lead wirklich löschen?</AlertDialogTitle>
          <AlertDialogDescription className="text-text-secondary">
            Möchten Sie den Lead <strong>{leadToDelete?.name}</strong> ({leadToDelete?.email}) wirklich löschen? 
            Diese Aktion kann nicht rückgängig gemacht werden.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-bg-tertiary text-text-primary hover:bg-border-primary">
            Abbrechen
          </AlertDialogCancel>
          <AlertDialogAction 
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={handleDelete}
          >
            Löschen
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
      </div>
    </div>
  );
};

export default AdminDashboard;